import { useEffect, useRef, useState } from "react";
import { Camera } from "lucide-react";
import { api } from "../../lib/api";
import { compressImage } from "../../lib/compressImage";
import { useStaff } from "./StaffLayout";

const STATUS_KEYS = { 未確認: "st_pending", 承認: "st_approved", 差戻し: "st_rejected" };
const CAT_KEYS = { 施工前: "cat_before", 施工中: "cat_during", 施工後: "cat_after" };

export default function StaffPhotos() {
  const { t } = useStaff();
  const [projects, setProjects] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [category, setCategory] = useState("施工中");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const fileRef = useRef(null);

  const load = () => {
    api.get("/staff/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
    api.get("/staff/photos").then((r) => setPhotos(r.data)).catch(() => setPhotos([]));
  };
  useEffect(load, []);

  const sites = projects.find((p) => p.id === projectId)?.sites || [];

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setPreview(f ? URL.createObjectURL(f) : "");
  };

  const submit = async () => {
    if (!file || !projectId) return;
    setSaving(true);
    setMessage("");
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file));
      fd.append("project_id", projectId);
      fd.append("site_id", siteId);
      fd.append("category", category);
      fd.append("title", title);
      fd.append("description", description);
      await api.post("/staff/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMessage(t("uploaded_ok"));
      setFile(null); setPreview(""); setTitle(""); setDescription("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch {
      setMessage(t("upload_failed"));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full border border-line bg-ink px-3 py-3 text-sm text-white outline-none focus:border-accent";

  return (
    <div data-testid="staff-photos-page" className="mx-auto max-w-lg space-y-8 px-4 py-6">
      <section className="border border-line bg-coal p-4">
        <h2 data-testid="staff-photo-upload-heading" className="font-mincho text-lg font-bold text-white">{t("upload_photo")}</h2>

        <div className="mt-4 space-y-3">
          <select data-testid="staff-photo-project" value={projectId} onChange={(e) => { setProjectId(e.target.value); setSiteId(""); }} className={inputCls}>
            <option value="">{t("select_project")}</option>
            {projects.map((p) => <option key={p.id} value={p.id}>[{p.code || "-"}] {p.name}</option>)}
          </select>
          <select data-testid="staff-photo-site" value={siteId} onChange={(e) => setSiteId(e.target.value)} className={inputCls}>
            <option value="">{t("select_site")}</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <div data-testid="staff-photo-category" className="flex gap-2">
            {Object.keys(CAT_KEYS).map((c) => (
              <button key={c} data-testid={`staff-photo-cat-${c}`} onClick={() => setCategory(c)} className={`flex-1 border py-2.5 text-xs font-bold transition-colors ${category === c ? "border-accent bg-accent/10 text-accent" : "border-line text-gray-400"}`}>
                {t(CAT_KEYS[c])}
              </button>
            ))}
          </div>
          <input data-testid="staff-photo-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("title_label")} className={inputCls} />
          <textarea data-testid="staff-photo-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("description")} rows={2} className={inputCls} />

          <button data-testid="staff-photo-pick" onClick={() => fileRef.current?.click()} className="flex w-full flex-col items-center gap-2 border border-dashed border-line py-6 text-gray-400 transition-colors hover:border-accent hover:text-accent">
            <Camera className="h-8 w-8" />
            <span className="text-xs">{t("take_or_choose")}</span>
          </button>
          <input ref={fileRef} data-testid="staff-photo-file" type="file" accept="image/*" className="hidden" onChange={pickFile} />
          {preview && <img data-testid="staff-photo-preview" src={preview} alt="" className="max-h-48 w-full object-cover" />}

          {message && <p data-testid="staff-photo-message" className={`text-sm ${message === t("uploaded_ok") ? "text-emerald-400" : "text-red-400"}`}>{message}</p>}
          <button data-testid="staff-photo-submit" onClick={submit} disabled={saving || !file || !projectId} className="w-full bg-accent py-4 text-sm font-bold text-ink transition-colors hover:bg-accent-hover disabled:opacity-40">
            {saving ? t("uploading") : t("submit")}
          </button>
        </div>
      </section>

      <section>
        <h2 data-testid="staff-my-photos-heading" className="font-mincho text-lg font-bold text-white">{t("my_photos")}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3">
          {photos.map((p) => (
            <div key={p.id} data-testid={`staff-photo-item-${p.id}`} className="border border-line bg-coal">
              <img src={`${api.defaults.baseURL}/site-photos/${p.id}/image`} alt={p.title} className="aspect-square w-full object-cover" />
              <div className="p-2">
                <p className="truncate text-xs font-bold text-white">{p.title || p.category}</p>
                <p className="mt-0.5 text-[10px] text-gray-500">{t(CAT_KEYS[p.category] || "cat_during")} ・ {t(STATUS_KEYS[p.status] || "st_pending")}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
